import React, { useState } from 'react';
import AccountantSidebar from './components/AccountantSidebar';
import Header from '../teacher_screen/components/Header';
import {
    User,
    Bell,
    Lock,
    Shield,
    CreditCard,
    Globe,
    ChevronRight,
    Search,
    Download,
    Mail,
    Phone,
    Plus,
    Check,
    Save
} from 'lucide-react';

export default function AccountantSettings() {
    const [activeTab, setActiveTab] = useState('Profile');

    const tabs = ['Profile', 'Payment Gateways', 'Notifications', 'Security'];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AccountantSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Settings"
                    subtitle="Configure your professional profile and financial workstation preferences"
                >
                    <button className="flex items-center gap-3 py-2.5 px-6 bg-blue-500 text-white rounded-xl text-[14px] font-bold shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all">
                        <Save size={18} />
                        Save Changes
                    </button>
                </Header>

                <div className="max-w-7xl mx-auto pb-20">
                    <div className="flex flex-col lg:flex-row gap-8">
                        {/* Sidebar Navigation */}
                        <div className="w-full lg:w-72 shrink-0">
                            <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm mb-8">
                                <div className="flex flex-col items-center text-center mb-8 pt-4">
                                    <div className="w-20 h-20 rounded-[28px] bg-blue-50 border-4 border-white shadow-xl flex items-center justify-center text-blue-500 mb-4 hover:scale-105 transition-transform cursor-pointer">
                                        <User size={32} strokeWidth={2.5} />
                                    </div>
                                    <h3 className="text-[18px] font-black text-[#111827] uppercase tracking-tighter leading-none mb-1.5">Finance Manager</h3>
                                    <p className="text-[11px] font-black text-gray-300 uppercase tracking-widest leading-none">Senior Accountant</p>
                                </div>
                                <div className="space-y-1.5">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab)}
                                            className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl text-[13px] font-black uppercase tracking-widest transition-all ${activeTab === tab
                                                ? 'bg-blue-500 text-white shadow-lg shadow-blue-200'
                                                : 'text-gray-400 hover:bg-gray-50 hover:text-gray-900'
                                                }`}
                                        >
                                            {tab}
                                            <ChevronRight size={14} className={activeTab === tab ? 'opacity-100' : 'opacity-0'} />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-[#111827] p-8 rounded-[32px] shadow-2xl shadow-blue-900/10 text-white relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500 opacity-20 blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:opacity-40 transition-opacity"></div>
                                <div className="relative z-10 text-center">
                                    <div className="w-12 h-12 bg-white/10 text-blue-400 rounded-2xl flex items-center justify-center mb-6 mx-auto"><Shield size={24} /></div>
                                    <h4 className="text-[16px] font-black uppercase tracking-tighter leading-none mb-2">2FA Protection</h4>
                                    <p className="text-gray-400 font-bold text-[11px] mb-6 leading-relaxed">Secure your financial station with biometric auth.</p>
                                    <button className="w-full py-3 bg-blue-500 text-white rounded-xl text-[12px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all">Enable Now</button>
                                </div>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1">
                            <div className="bg-white p-10 rounded-[32px] border border-gray-100 shadow-sm relative overflow-hidden mb-10">
                                <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-50">
                                    <h3 className="text-[20px] font-black text-[#111827] uppercase tracking-tighter">Profile Configuration</h3>
                                    <span className="px-3 py-1 bg-blue-50 text-blue-500 rounded-lg text-[10px] font-black uppercase tracking-widest">Active Station</span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                                    <div className="space-y-6">
                                        <SettingItem label="Full Legal Name" value="Institutional Accountant" icon={User} />
                                        <SettingItem label="Professional Email" value="finance@nerdos.in" icon={Mail} />
                                    </div>
                                    <div className="space-y-6">
                                        <SettingItem label="Employee ID" value="NERD-FIN-102" icon={Shield} />
                                        <SettingItem label="Direct Workflow" value="+91 80000 12345" icon={Phone} />
                                    </div>
                                </div>

                                <div className="pt-10 border-t border-gray-50">
                                    <h4 className="text-[13px] font-black text-gray-400 uppercase tracking-widest mb-8">Workstation Preferences</h4>
                                    <div className="grid grid-cols-1 gap-4">
                                        <ToggleItem label="Automated Invoice Generation" desc="Email receipt automatically after payment recording." active={true} />
                                        <ToggleItem label="Consolidated Daily Report" desc="Receive end-of-day SMS summary of all collections." active={false} />
                                        <ToggleItem label="Multi-Factor Login" desc="Require OTP for every administrative login session." active={true} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function SettingItem({ label, value, icon: Icon }) {
    return (
        <div className="space-y-2 group">
            <label className="text-[11px] font-black text-gray-300 uppercase tracking-widest ml-1">{label}</label>
            <div className="h-12 bg-[#F8FAFC] border border-transparent rounded-xl px-4 flex items-center justify-between focus-within:bg-white focus-within:border-blue-500 transition-all shadow-sm">
                <span className="text-[14px] font-bold text-[#111827]">{value}</span>
                <Icon size={16} className="text-gray-300 group-focus-within:text-blue-500 transition-colors" />
            </div>
        </div>
    );
}

function ToggleItem({ label, desc, active }) {
    return (
        <div className="flex items-center justify-between gap-10 group bg-[#F8FAFC] p-5 rounded-2xl hover:bg-blue-50/50 transition-all cursor-pointer border border-transparent hover:border-blue-100">
            <div className="flex-1">
                <h5 className="text-[14px] font-black text-[#111827] uppercase tracking-tight leading-none mb-1.5">{label}</h5>
                <p className="text-[12px] font-bold text-gray-400 italic leading-tight">{desc}</p>
            </div>
            <div className={`w-12 h-7 rounded-full p-1 transition-all ${active ? 'bg-blue-500' : 'bg-gray-200'} relative shrink-0`}>
                <div className={`w-5 h-5 bg-white rounded-full transition-all shadow-md transform ${active ? 'translate-x-5' : 'translate-x-0'}`}></div>
            </div>
        </div>
    );
}
